import { Box, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import { tokens } from '../../../global/theme/tokens';
import { useMethodColor } from '../weekly-cal-view/time-functions/useMethodColors';
import { renderHours } from '../../../scenes/time-tracking/worktime';
import { useTranslation } from 'react-i18next';

const BlockedTimeBox = ({
  blockedTime,
  index = 0,
  dataCollection,
  isSelection = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const colorToUse = useMethodColor('edit');
  const { t } = useTranslation();

  const dayStart = moment(dataCollection.startDate).startOf('day').hour(6);
  const dayEnd = moment(dataCollection.startDate).hour(19).minute(0).second(0);

  const start = moment.max(moment(blockedTime.startTime), dayStart);
  const end = moment.min(moment(blockedTime.endTime), dayEnd);

  const isWholeDay = blockedTime.wholeDay === true;

  const color = isSelection ? colors.info : colors.error;
  const bgColor = `${color}40`;
  const borderColor = color;
  const labelPrefix = isWholeDay
    ? isSelection
      ? 'Selected (All Day)'
      : t('Blocked (All Day)')
    : isSelection
    ? 'Selected'
    : t('Blocked');

  // Whole day — no change
  if (isWholeDay) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: `${index * 24}px`,
          left: 0,
          width: '100%',
          height: 22,
          backgroundColor: `${bgColor}`,
          borderLeft: `3px solid ${borderColor}`,
          borderRadius: '4px',
          zIndex: isSelection ? 1 : 0,
          padding: '2px 6px',
          display: 'flex',
          alignItems: 'center',
          fontSize: '11px',
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: 'pointer',
        }}
      >
        {labelPrefix}
      </Box>
    );
  }

  // Skip invalid times
  if (!start.isValid() || !end.isValid() || !start.isBefore(end)) return null;

  const startPos = dateToPixel(start);
  const endPos = dateToPixel(end);
  const height = endPos - startPos;
  const timeInMinutes = end.diff(start, 'minutes');

  // If Selection: Use your full custom selection styling
  if (isSelection) {
    return (
      <Box
        className="absolute flex px-2 w-full"
        style={{
          top: `${startPos}px`,
          height: `${height}px`,
          width: '100%',
          zIndex: 1,
          paddingLeft: '8px',
          paddingRight: '6px',
        }}
      >
        <Box
          className="relative h-full w-full rounded-lg flex justify-center items-center flex-col backdrop-blur-sm"
          sx={{
            bgcolor: colorToUse.primary,
            borderColor: colorToUse.secondary,
            borderWidth: `1px`,
          }}
        >
          <Box
            className="px-3 py-2 rounded-lg cursor-default z-10 backdrop-blur-sm"
            sx={{
              bgcolor: colors.glass,
              opacity: height >= 50 ? 1 : 0,
              transition: 'opacity 0.3s',
            }}
          >
            <Typography variant="h4" textAlign="center" className="monospace">
              {renderHours(timeInMinutes)} h
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // Default rendering (non-selection)
  return (
    <Box
      sx={{
        position: 'absolute',
        top: startPos,
        height: height,
        left: 0,
        width: '100%',
        backgroundColor: bgColor,
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: '4px',
        zIndex: 0,
        cursor: 'pointer',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: '2px 6px',
        fontSize: '11px',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: '10px',
          fontWeight: 500,
          opacity: 0.8,
        }}
      >
        {labelPrefix}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          fontSize: '10px',
          opacity: 0.6,
          fontWeight: 500,
        }}
      >
        {start.format('HH:mm')} - {end.format('HH:mm')}
      </Typography>
    </Box>
  );

  // Converts time to pixel position
  function dateToPixel(date) {
    const hourStart = 6;
    const hourEnd = 19;
    const resHeight = 30;
    const res = 2;

    const minutesPerSegment = 60 / res;
    let minutes = date.hours() * 60 + date.minutes() + date.seconds() / 60;

    const startMinutes = hourStart * 60;
    const endMinutes = hourEnd * 60;

    if (minutes < startMinutes) minutes = startMinutes;
    if (minutes > endMinutes) minutes = endMinutes;

    const numOfSegments = (minutes - startMinutes) / minutesPerSegment;
    return numOfSegments * resHeight + resHeight;
  }
};

export default BlockedTimeBox;
